
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, FolderArchive, HardDrive } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { downloadBlob, getBackupDirectoryPath } from "@/utils/downloadUtils";

export const PluginDownloadSection = () => {
  const [downloadStarted, setDownloadStarted] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadPlugin = () => {
    setIsDownloading(true);
    
    // In a real app, this would trigger the download of the actual WordPress plugin
    // For the demo, we'll use the PHP file in the public directory
    
    fetch('/wordpressPlugin.php')
      .then(response => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.blob();
      })
      .then(blob => {
        // Use our utility function to download the file
        downloadBlob(blob, 'backup-site-hero.php');
        
        setDownloadStarted(true);
        toast({
          title: "Download started",
          description: "The WordPress plugin is being downloaded. Please install it on your WordPress site.",
        });
      })
      .catch(error => {
        console.error("Error downloading plugin:", error);
        toast({
          title: "Download failed",
          description: "There was an error downloading the plugin. Please try again or use the direct link below.",
          variant: "destructive",
        });
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">1. Download and Install the Plugin</h3>
      <p className="text-gray-500 text-sm">
        Download our WordPress plugin and install it on your WordPress site through the admin dashboard.
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <Button 
          onClick={handleDownloadPlugin} 
          disabled={isDownloading}
          className="mt-2"
        >
          <Download className="mr-2 h-4 w-4" />
          {isDownloading ? "Downloading..." : downloadStarted ? "Download Again" : "Download Plugin"}
        </Button>
        
        <Button 
          variant="outline" 
          className="mt-2"
          asChild
        >
          <a 
            href="/wordpressPlugin.php" 
            download="backup-site-hero.php"
            className="inline-flex items-center"
          >
            <Download className="mr-2 h-4 w-4" />
            Direct Download Link
          </a>
        </Button>
      </div>
      
      {downloadStarted && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
          <p className="font-medium">Plugin downloaded successfully!</p>
          <p className="mt-1">
            Now upload and activate the plugin through your WordPress admin dashboard at 
            <span className="font-mono mx-1">Plugins → Add New → Upload Plugin</span>
          </p>
          <div className="mt-2 space-y-2">
            <p className="flex items-center">
              <FolderArchive className="h-4 w-4 mr-2" />
              <span>Backups will be stored in the <span className="font-mono">wp-content/uploads/backups</span> folder on your server</span>
            </p>
            <p className="flex items-center">
              <HardDrive className="h-4 w-4 mr-2" />
              <span>The plugin will create real backup files containing:</span>
            </p>
            <ul className="list-disc ml-10 text-sm">
              <li>WordPress core configuration files</li>
              <li>Theme files</li>
              <li>Plugin files</li>
              <li>Uploaded media</li>
              <li>Database SQL dump</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
